

import { Eye, Pencil, Package, BookOpen, Trash2, CheckSquare, Square } from "lucide-react";
import Badge from "../../components/Badge";
import { formatCurrency } from "@/lib/data";
import type { Vendor } from "../types";

interface VendorsTableProps {
  vendors: Vendor[];
  selectedVendors: Set<number>;
  isStaff: boolean;
  canDelete: boolean;
  onToggleSelection: (id: number) => void;
  onToggleAll: () => void;
  onViewDetails: (id: number) => void;
  onOpenServices: (vendor: Vendor) => void;
  onOpenLedger: (vendor: Vendor) => void;
  onEdit: (vendor: Vendor) => void;
  onDelete: (vendor: Vendor) => void;
}

export function VendorsTable({
  vendors,
  selectedVendors,
  isStaff,
  canDelete,
  onToggleSelection,
  onToggleAll,
  onViewDetails,
  onOpenServices,
  onOpenLedger,
  onEdit,
  onDelete,
}: VendorsTableProps) {
  if (vendors.length === 0) {
    return (
      <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
        <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
        <p className="text-stone-500">No vendors found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {!isStaff && (
                <th className="text-center px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider w-10">
                  <button
                    onClick={onToggleAll}
                    className="hover:text-amber-600 transition-colors"
                  >
                    {selectedVendors.size === vendors.length && vendors.length > 0 ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
              )}
              <th className="text-left px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Name</th>
              <th className="text-left px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider hidden md:table-cell">Type</th>
              <th className="text-left px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider hidden lg:table-cell">Contact</th>
              <th className="text-left px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Phone</th>
              <th className="text-right px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Assigned</th>
              <th className="text-right px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Paid</th>
              <th className="text-right px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Balance</th>
              <th className="text-center px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</th>
              <th className="text-right px-3 sm:px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {vendors.map((vendor) => {
              const isSelected = selectedVendors.has(vendor.id);
              return (
                <tr key={vendor.id} className={`hover:bg-stone-50 transition-colors ${isSelected ? 'bg-amber-50' : ''}`}>
                  {!isStaff && (
                    <td className="px-3 sm:px-4 py-3 text-center">
                      <button
                        onClick={() => onToggleSelection(vendor.id)}
                        className="hover:text-amber-600 transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-amber-600" />
                        ) : (
                          <Square className="w-4 h-4 text-stone-400" />
                        )}
                      </button>
                    </td>
                  )}
                  <td className="px-3 sm:px-4 py-3">
                    <div 
                      className="font-medium text-stone-800 cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => onViewDetails(vendor.id)}
                      title="Click to view details"
                    >
                      {vendor.name}
                    </div>
                    <div className="text-xs text-stone-400 md:hidden mt-0.5">{vendor.vendor_type?.name}</div>
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-sm text-stone-600 hidden md:table-cell">{vendor.vendor_type?.name}</td>
                  <td className="px-3 sm:px-4 py-3 text-sm text-stone-600 hidden lg:table-cell">{vendor.contact_person}</td>
                  <td className="px-3 sm:px-4 py-3 text-sm text-stone-600">{vendor.phone}</td>
                  <td className="px-3 sm:px-4 py-3 text-right font-medium text-stone-700">{formatCurrency(vendor.total_assigned || 0)}</td>
                  <td className="px-3 sm:px-4 py-3 text-right font-medium text-emerald-600">{formatCurrency(vendor.total_paid || 0)}</td>
                  <td className="px-3 sm:px-4 py-3 text-right font-medium text-amber-700">{formatCurrency((vendor.total_assigned || 0) - (vendor.total_paid || 0))}</td>
                  <td className="px-3 sm:px-4 py-3 text-center"><Badge label={vendor.status} status={vendor.status} /></td>
                  <td className="px-3 sm:px-4 py-3">
                    {!isStaff && (
                      <div className="flex justify-end gap-1">
                        <button 
                          onClick={() => onViewDetails(vendor.id)}
                          className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                          title="Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => onOpenServices(vendor)}
                          className="p-2 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                          title="Services"
                        >
                          <Package className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => onOpenLedger(vendor)}
                          className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                          title="Ledger"
                        >
                          <BookOpen className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => onEdit(vendor)}
                          className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {canDelete && (
                          <button 
                            onClick={() => onDelete(vendor)}
                            className="p-2 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
